import { ComponentProps } from '@stitches/react'
import { Icon } from 'common/Icon'
import { PopupDialog } from 'common/PopupDialog'
import { SnackBar } from 'common/SnackBar'
import snackBar from 'common/SnackBar/config'
import { CommentsDrawer } from 'containers/PostModal/components/MobileSideActions/CommentDrawer'
import { PostType } from 'graphql/types'
import { ShareSocialType } from 'interfaces'
import { formatNumberAsCurrency } from 'lib/helpers'
import { useStore } from 'lib/store'
import { theme } from 'lib/theme'
import { NSFW_EVENT } from 'lib/tracking/types'
import { observer } from 'mobx-react-lite'
import { Post } from 'models/Post'
import { Button } from 'primitives/Button'
import { Flex } from 'primitives/Flex'
import { useRef, useState } from 'react'
import { MouseEvent } from 'react'
import { toast } from 'components/common/Toast'
import { PostIconButton } from './IconButton'
import { PostShareMenu } from './ShareMenu'

export interface PostBottomBarProps extends ComponentProps<typeof Flex> {
  post: Post
  handlePayment: () => void
  hideCommentIcon?: boolean
}

const _PostBottomBar: React.FC<PostBottomBarProps> = ({
  css,
  post,
  hideCommentIcon = false,
  handlePayment
}) => {
  const [shareMenuIsOpen, setShareMenuIsOpen] = useState(false)
  const [isCommentsOpen, setIsCommentsOpen] = useState(false)
  const store = useStore()

  const postCommentsRef = useRef<HTMLDivElement>(null)

  const onAddComment = () => {
    if (postCommentsRef.current) {
      postCommentsRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
  const paymentButtonLabel = () => {
    // TODO: PostType.EXCLUSIVE && post.IsVisible (post was paid and unlocked) = > tip post
    switch (true) {
      case post.type === PostType.EXCLUSIVE && !post.isVisible:
        return formatNumberAsCurrency(post.price!)
      case post.type === PostType.SUBSCRIPTION && !post.isSubscribed:
        return 'Subscribe'
      case post.type === PostType.TEASER:
      case post.type === PostType.SUBSCRIPTION && post.isSubscribed:
      case post.type === PostType.EXCLUSIVE && post.isVisible:
        return 'Tip'
      default:
        return 'Unkonwn post type'
    }
  }

  const togglePostLike = async (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
    if (store.ui.handleGatedAction()) return

    const button = e.target as HTMLButtonElement
    button.disabled = true
    try {
      await post.toggleLikePost()
    } catch (err) {
      // toast(
      //   () => <SnackBar title={`Unable to ${post.isLiked ? 'unlike' : 'like'} post`} />,
      //   snackBar
      // )
    }
    button.disabled = false
  }

  const handleCommentsClick = async () => {
    if (store.ui.isMobile) {
      // Mobile comments click does not toggle the PostModal (which detects isIntersecting and loads comments).
      // Fetch initial comments for post if required.
      post.fetchInitialComments()
      setIsCommentsOpen(true)
    } else {
      store.feed.setSelectedPost(post)
      store.ui.openPostModal()
    }

    store.analytics.track(NSFW_EVENT.FEED_POST_COMMENTS_VIEW, {
      feedType: post.feedType,
      page: post.feedPage,
      postId: post.postId
    })
  }

  const handleSharedPost = (type: ShareSocialType) => {
    store.analytics.track(NSFW_EVENT.FEED_POST_SHARE, {
      feedType: post.feedType,
      page: post.feedPage,
      postId: post.postId,
      sharedSocial: type
    })
  }

  return (
    <Flex
      css={{
        backgroundColor: theme.colors.gray300,
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0px 16px',
        '@lg': {
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
          ...css?.['@lg']
        },
        ...css
      }}
    >
      <Flex
        css={{
          backgroundColor: theme.colors.gray300
        }}
      >
        <PostIconButton
          buttonIcon="Heart"
          onClick={togglePostLike}
          css={{
            marginRight: '24px',
            ...(post.isLiked && {
              '& svg > path': {
                fill: theme.colors.red100,
                stroke: theme.colors.red100
              },
              '&:hover svg > path': {
                stroke: theme.colors.red100
              }
            })
          }}
        >
          {post.likeCount}
        </PostIconButton>
        {!hideCommentIcon && (
          <PostIconButton
            buttonIcon="Comment"
            onClick={handleCommentsClick}
            css={{
              marginRight: '24px'
            }}
          >
            {post.commentCount}
          </PostIconButton>
        )}
        <PostIconButton
          buttonIcon="Share"
          onClick={() => setShareMenuIsOpen(true)}
          css={{
            marginRight: '24px'
          }}
        />
      </Flex>
      {!post.isOwner && (
        <>
          {/* Unlock */}
          {/* TODO: Should not show unlock if this has already been paid for; Should be tip */}

          <Button
            theme={'primary'}
            pill
            css={{
              display: 'flex',
              flexDirection: 'row',
              gap: '3px',
              width: 'auto',
              border: `1px solid ${theme.colors.gray500}`,
              fontSize: '12px',
              lineHeight: '16px',
              alignItems: 'center',
              textAlign: 'center',
              '&:hover svg > .primary.fill': {
                fill: theme.colors.gray800
              }
            }}
            onClick={() => handlePayment()}
          >
            {post.type == PostType.EXCLUSIVE && !post.isVisible && (
              <Icon
                icon="Lock"
                css={{
                  width: 14,
                  marginTop: '-2px',
                  '&:hover': {
                    '.primary fill': {
                      fill: theme.colors.gray800
                    }
                  }
                }}
              />
            )}
            {paymentButtonLabel()}
          </Button>
        </>
      )}
      <PopupDialog isOpen={shareMenuIsOpen} onIgnore={() => setShareMenuIsOpen(false)}>
        <PostShareMenu post={post} onShare={handleSharedPost} />
      </PopupDialog>
      <CommentsDrawer
        isOpen={isCommentsOpen}
        post={post}
        commentRef={postCommentsRef}
        onClose={() => setIsCommentsOpen(false)}
        onAddComment={onAddComment}
      />
    </Flex>
  )
}

export const PostBottomBar = observer(_PostBottomBar)
